import * as React from "react";
import { IEventBus, IEventSource, Subscription } from "./events";

/**
 * Context for providing an event source or event bus to child components
 */
const EventSourceContext = React.createContext<IEventSource | IEventBus<any> | null>(null);

/**
 * Provider component to make an event source or event bus available to child components
 */
export const EventSourceProvider = <TSource extends IEventSource | IEventBus<any>>({
    source,
    children
}: {
    source: TSource;
    children: React.ReactNode;
}) => {
    return (
        <EventSourceContext.Provider value={source}>
            {children}
        </EventSourceContext.Provider>
    );
};

/**
 * Hook to access the event source or event bus directly.
 * Most components should use useEvent instead.
 */
export function useEventSource<TSource extends IEventSource | IEventBus<any>>(): TSource {
    const source = React.useContext(EventSourceContext);
    if (!source) {
        throw new Error("useEventSource must be used within an EventSourceProvider");
    }
    return source as TSource;
}

/**
 * Generic hook to subscribe to events from any event source or event bus.
 * Automatically handles subscription cleanup on unmount or dependency changes.
 * 
 * @param subscribe - Function that receives the event source/bus and returns a subscription
 * @param deps - Dependency array for the subscription (similar to useEffect)
 * 
 * @example
 * // Listen to a specific event
 * useEvent<EventBus>(bus => bus.namespaces.excel.events.worksheetActivated.on((payload) => {
 *   console.log("Worksheet activated:", payload.worksheetName);
 * }));
 * 
 * @example
 * // Listen to multiple event types
 * useEvent<EventBus>(bus => bus.namespaces.navigation.on(["worksheetChanged", "selectionChanged"], (payload, event) => {
 *   console.log("Navigation:", event.type, payload);
 * }));
 * 
 * @example
 * // Listen to all events in a namespace
 * useEvent<EventBus>(bus => bus.namespaces.excel.on((payload, event) => {
 *   console.log("Excel event:", event.type);
 * }));
 * 
 * @example
 * // Conditional subscription using the dependency array
 * useEvent<EventBus>(bus => 
 *   enabled 
 *     ? bus.namespaces.excel.events.worksheetActivated.on(handleActivation)
 *     : { id: 'noop', unsubscribe: () => true }
 * , [enabled]);
 */
export function useEvent<TEventSource extends IEventSource | IEventBus<any>>(
    subscribe: (source: TEventSource) => Subscription,
    deps: React.DependencyList = []
): void {
    const source = useEventSource<TEventSource>();

    React.useEffect(() => {
        const subscription = subscribe(source);
        
        return () => {
            subscription.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, ...deps]);
}

/**
 * Hook to capture and maintain the latest payload from events.
 * Updates component state whenever a matching event is received.
 * 
 * The subscribe function receives both the event source AND a listener callback.
 * Use the listener callback to update the state with the payload you want to track.
 * 
 * @param subscribe - Function that receives the event source/bus and a listener callback,
 *                    returns a subscription
 * @param deps - Dependency array for the subscription
 * @returns The latest payload or undefined if no event has been received yet
 * 
 * @example
 * // Track the latest worksheet activation
 * const latestWorksheet = useLatestEvent<
 *   { workbookId: string; worksheetId: string; worksheetName: string },
 *   EventBus
 * >(
 *   (bus, listener) => bus.namespaces.excel.events.worksheetActivated.on(listener)
 * );
 * // latestWorksheet will be: { workbookId, worksheetId, worksheetName } | undefined
 * 
 * @example
 * // Track the latest event from multiple event types (all update the same state)
 * const latestNav = useLatestEvent<NavigationPayload, EventBus>(
 *   (bus, listener) => bus.namespaces.navigation.on(["worksheetChanged", "selectionChanged"], listener)
 * );
 * 
 * @example
 * // Transform the payload before storing
 * const workbookName = useLatestEvent<string, EventBus>(
 *   (bus, listener) => bus.namespaces.excel.events.workbookOpened.on((payload) => {
 *     listener(payload.workbookName); // Only store the name, not the full payload
 *   })
 * );
 */
export function useLatestEvent<TPayload, TEventSource extends IEventSource | IEventBus<any>>(
    subscribe: (source: TEventSource, listener: (payload: TPayload) => void) => Subscription,
    deps: React.DependencyList = []
): TPayload | undefined {
    const [payload, setPayload] = React.useState<TPayload | undefined>(undefined);
    const source = useEventSource<TEventSource>();

    React.useEffect(() => {
        const subscription = subscribe(source, (newPayload: TPayload) => {
            setPayload(newPayload);
        });

        return () => {
            subscription.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, ...deps]);

    return payload;
}
